import axios from 'axios';
import { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import secureStorage from './secureStorage';

const API = `${API_BASE_URL}/api`;

/**
 * Fetch a HIRA thumbnail (returns the base64 data URI string) or null if
 * the entry has no image / caller cannot access it. Always resolves — never
 * throws — so the caller can wire it directly into a lazy image loader.
 */
export async function fetchHiraThumbnail(hiraId) {
  if (!hiraId) return null;
  try {
    const token = secureStorage.getToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const { data } = await axios.get(`${API}/hira/${hiraId}/thumbnail`, { headers });
    return data?.image || null;
  } catch (_err) {
    return null;
  }
}

/**
 * Fetch an exercise's own uploaded image (list endpoint strips it for
 * Cloudflare 520 protection, so we lazy-load it per-row from a dedicated
 * lightweight endpoint). Resolves to null on any failure.
 */
export async function fetchExerciseImage(exerciseId) {
  if (!exerciseId) return null;
  try {
    const token = secureStorage.getToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const { data } = await axios.get(`${API}/exercise-builder/${exerciseId}/exercise-image`, { headers });
    return data?.image || null;
  } catch (_err) {
    return null;
  }
}

/**
 * React hook: given an array of hira_ids, lazily fetches each thumbnail
 * once and returns a { [hiraId]: dataUri } cache. IDs already in the cache
 * are skipped so re-renders don't re-fetch.
 */
export function useHiraThumbnailCache(hiraIds) {
  const [cache, setCache] = useState({});

  useEffect(() => {
    const missing = (hiraIds || []).filter((id) => id && !(id in cache));
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      for (const id of missing) {
        const image = await fetchHiraThumbnail(id);
        if (cancelled) return;
        setCache((prev) => ({ ...prev, [id]: image }));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Array.isArray(hiraIds) ? hiraIds.join(',') : '']);

  return cache;
}

/**
 * React hook: given an array of exercise objects (each with .id and
 * .has_exercise_image), lazily fetches the exercise image for every row
 * flagged has_exercise_image=true and returns a { [exerciseId]: dataUri }
 * cache. Rows without has_exercise_image are skipped entirely so we don't
 * fire 404 requests for exercises that never had their own image.
 */
export function useExerciseImageCache(exercises) {
  const [cache, setCache] = useState({});

  const idsToFetch = (exercises || [])
    .filter((e) => e && e.has_exercise_image && e.id && !(e.id in cache))
    .map((e) => e.id);

  useEffect(() => {
    if (idsToFetch.length === 0) return;
    let cancelled = false;
    (async () => {
      for (const id of idsToFetch) {
        const image = await fetchExerciseImage(id);
        if (cancelled) return;
        setCache((prev) => ({ ...prev, [id]: image }));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsToFetch.join(',')]);

  return cache;
}

